import { useMemo, useState } from "react";
import { Stack, Table, Tbody, Td, Text, Tr } from "@/components/ui";
import { formatearDetallesAmigables } from "../utils/format-detalles-amigables";
import { prettyJson } from "../utils/format-helpers";

/**
 * Muestra los detalles de un evento de auditoria en formato amigable
 * (etiqueta · valor traducido al español). Incluye un toggle al final
 * para revelar el JSON tecnico crudo · util para auditores que necesitan
 * verificar los campos originales (ej. al exportar reportes).
 */
export function DetallesAmigables({
  detallesJson,
  density = "comodo",
}: {
  detallesJson: string | null | undefined;
  density?: "comodo" | "compacto";
}) {
  const items = useMemo(
    () => formatearDetallesAmigables(detallesJson),
    [detallesJson]
  );
  const [verJsonCrudo, setVerJsonCrudo] = useState(false);

  if (items.length === 0) {
    return (
      <Text size="sm" c="dimmed" className="italic">
        Sin detalles adicionales.
      </Text>
    );
  }

  const size = density === "compacto" ? "xs" : "sm";

  return (
    <Stack gap="xs">
      <Table withRowBorders={false} verticalSpacing={density === "compacto" ? "xs" : "sm"}>
        <Tbody>
          {items.map((item, idx) => (
            <Tr key={`${item.etiqueta}-${idx}`}>
              <Td className="w-2/5 !pl-0 align-top">
                <Text size={size} c="dimmed">
                  {item.etiqueta}
                </Text>
              </Td>
              <Td className="align-top">
                <Text size={size} fw={500} className="break-words">
                  {item.valor}
                </Text>
                {item.sub ? (
                  <Text size="xs" c="dimmed" className="mt-0.5 font-mono">
                    {item.sub}
                  </Text>
                ) : null}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      {/* Toggle para el JSON tecnico · oculto por default · solo para auditores que necesiten verificar el dato crudo. */}
      <div>
        <button
          type="button"
          onClick={() => setVerJsonCrudo((v) => !v)}
          className="cursor-pointer select-none text-xs text-muted-foreground hover:underline"
        >
          {verJsonCrudo ? "Ocultar formato técnico" : "Ver formato técnico (JSON)"}
        </button>
        {verJsonCrudo && detallesJson ? (
          <pre className="mt-1 max-h-60 overflow-auto rounded-md border border-border bg-muted/40 p-2 text-[10px] whitespace-pre-wrap break-all">
            {prettyJson(detallesJson)}
          </pre>
        ) : null}
      </div>
    </Stack>
  );
}
